import { CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { FieldSources, type FieldSource } from "./field-sources";
import { useBulkEditMaybe } from "./bulk-edit-context";

/**
 * Base wrapper: renders a label + read-only value row by default. While the
 * surrounding region's bulk-edit mode is on (see <BulkEditProvider>), the
 * editor slot replaces the read value and stages user changes via each
 * primitive's useStageDraftInBulk; the parent's Save / Cancel control drives
 * the actual persistence.
 *
 * Per-field click-to-edit was removed in favour of bulk-only editing — see
 * SAAS-736. The Verify action stays available in read mode for one-click
 * approval of pending AI fields without entering edit at all.
 */
export type FieldStatus = "approved" | "pending" | "missing" | null;

export interface EditableFieldProps {
  label: string;
  /** Read-mode rendering. If null/empty the field shows an em-dash. */
  readValue: ReactNode;
  /** Edit-mode rendering, used when the surrounding bulk-edit mode is on. */
  editor: ReactNode;
  /**
   * Called when bulk-edit mode flips on so each primitive can seed its local
   * draft from the latest value (between bulk sessions, after a save, etc.).
   */
  onEnterEdit: () => void;
  multiline?: boolean;
  /** Disable the field entirely (e.g. no workspace context). */
  disabled?: boolean;
  /** Review workflow status for the field (approved / pending / missing). */
  status?: FieldStatus;
  /**
   * One-click approve handler — shown in read mode only when status is
   * "pending". Saves the field's current value so the BE flips field_state
   * to "approved" without requiring an edit.
   */
  onApprove?: () => Promise<unknown> | void;
  /**
   * AI source provenance for this field (BE populates dealInfo.deal[].sources).
   * When non-empty, a small ·N superscript renders next to the label with a
   * hover popover listing each source.
   */
  sources?: FieldSource[];
}

const STATUS_META: Record<
  NonNullable<FieldStatus>,
  { dot: string; label: string }
> = {
  approved: { dot: "bg-emerald-500", label: "Approved" },
  pending: { dot: "bg-purple-500", label: "Pending review" },
  missing: { dot: "bg-rose-500", label: "Missing info" },
};

export function FieldStatusDot({ status }: { status: FieldStatus }) {
  if (!status) return null;
  const meta = STATUS_META[status];
  return (
    <span
      className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", meta.dot)}
      title={meta.label}
    />
  );
}

export function EditableField({
  label,
  readValue,
  editor,
  onEnterEdit,
  multiline,
  disabled,
  status,
  onApprove,
  sources,
}: EditableFieldProps) {
  const [isApproving, setIsApproving] = useState(false);
  const bulk = useBulkEditMaybe();
  const inBulk = bulk?.enabled ?? false;

  // When bulk mode flips on, run onEnterEdit so each primitive seeds its
  // local draft from the latest value.
  useEffect(() => {
    if (inBulk) onEnterEdit();
    // onEnterEdit is intentionally not in deps — primitives recreate it
    // each render but its semantics (sync draft from value) don't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inBulk]);

  const handleApprove = async () => {
    if (!onApprove || isApproving) return;
    setIsApproving(true);
    try {
      await onApprove();
    } catch {
      // toast is fired by the hook
    } finally {
      setIsApproving(false);
    }
  };

  const showEmpty =
    !inBulk &&
    (readValue === null || readValue === undefined || readValue === "");

  return (
    <div
      className={cn(
        "group flex px-4",
        multiline
          ? "flex-col gap-4 py-3"
          : "flex-col gap-1.5 py-2 sm:flex-row sm:items-start sm:gap-4"
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground",
          multiline ? "" : "sm:w-36 sm:pt-1.5"
        )}
      >
        {label}
        <FieldStatusDot status={status ?? null} />
        <FieldSources sources={sources} />
      </span>

      {inBulk ? (
        <div className="min-w-0 flex-1">{editor}</div>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div
            className={cn(
              "min-w-0 flex-1 text-sm",
              showEmpty ? "text-muted-foreground/40" : "text-foreground"
            )}
          >
            {showEmpty
              ? status === "missing"
                ? "No info available, enter manually"
                : "—"
              : readValue}
          </div>
          {!disabled && status === "pending" && onApprove && (
            // Per spec (SAAS-661): Verify is always visible — not hover-only —
            // so users see at a glance which rows need an action.
            <button
              type="button"
              onClick={handleApprove}
              disabled={isApproving}
              className="flex h-6 shrink-0 items-center gap-1 rounded px-1.5 text-[11px] font-medium text-purple-600 transition-colors hover:bg-purple-500/10 disabled:opacity-50"
              title={`Verify ${label}`}
            >
              {isApproving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3 w-3" />
              )}
              Verify
            </button>
          )}
        </div>
      )}
    </div>
  );
}
